/**
 * Light / dark theme, applied app-wide before React renders.
 *
 * It used to be set by Layout, which only wraps signed-in pages, so the login
 * and loading screens always rendered light, and the system setting was read
 * once and never followed afterwards.
 *
 * With no saved choice the theme follows the operating system, live. Toggling
 * saves an explicit choice; toggling back to whatever the system prefers clears
 * it, so the app follows the system again rather than pinning it forever.
 */

const STORAGE_KEY = 'theme'
const query = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null)

function savedTheme() {
  try { return localStorage.getItem(STORAGE_KEY) } catch { return null }
}

export function systemPrefersDark() {
  return Boolean(query()?.matches)
}

export function isDarkTheme() {
  const saved = savedTheme()
  return saved === 'dark' || (saved !== 'light' && systemPrefersDark())
}

function apply() {
  const dark = isDarkTheme()
  document.documentElement.classList.toggle('dark', dark)
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light'
  window.dispatchEvent(new CustomEvent('themechange', { detail: { dark } }))
}

export function setDarkTheme(dark) {
  try {
    if (dark === systemPrefersDark()) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light')
  } catch { /* Storage may be disabled; the class change still applies. */ }
  apply()
}

/** Apply now and follow system changes while no explicit choice is saved. */
export function initTheme() {
  apply()
  query()?.addEventListener?.('change', () => { if (!savedTheme()) apply() })
}
